import { Accept } from 'react-dropzone/.';

/**
 * Mapping from short file extension keys to their MIME type accept entries.
 * Each key maps to a record of MIME type → extensions[].
 */
const MIME_MAP: Record<string, Record<string, string[]>> = {
  pdf: { 'application/pdf': ['.pdf'] },
  zip: { 'application/zip': ['.zip'] },
  jpg: { 'image/jpeg': ['.jpg', '.jpeg'] },
  jpeg: { 'image/jpeg': ['.jpg', '.jpeg'] },
  png: { 'image/png': ['.png'] },
  gif: { 'image/gif': ['.gif'] },
  webp: { 'image/webp': ['.webp'] },
  svg: { 'image/svg+xml': ['.svg'] },
  csv: { 'text/csv': ['.csv'] },
  xls: { 'application/vnd.ms-excel': ['.xls'] },
  xlsx: {
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
  },
  doc: { 'application/msword': ['.doc'] },
  docx: {
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
  },
};

/**
 * Parses a comma-separated string of file extensions into a react-dropzone Accept object.
 *
 * @example
 * // Env: REACT_APP_DRIVER_PERMISSION_ACCEPTED_FORMATS=pdf
 * parseFileFormats('pdf')
 * // → { "application/pdf": [".pdf"] }
 *
 * @example
 * parseFileFormats('jpg,png')
 * // → { "image/jpeg": [".jpg", ".jpeg"], "image/png": [".png"] }
 *
 * @example
 * parseFileFormats('zip')
 * // → { "application/zip": [".zip"], "application/x-zip-compressed": [".zip"] }
 */
export const parseFileFormats = (formats: string): Accept => {
  const keys = formats
    .split(',')
    .map((s) => s.trim().toLowerCase())
    .filter(Boolean);

  const result: Accept = {};

  for (const key of keys) {
    const entry = MIME_MAP[key];
    if (entry) {
      for (const [mime, exts] of Object.entries(entry)) {
        if (result[mime]) {
          // Merge extensions, avoiding duplicates
          const existing = new Set(result[mime]);
          for (const ext of exts) existing.add(ext);
          result[mime] = [...existing];
        } else {
          result[mime] = [...exts];
        }
      }
    }
  }

  return result;
};
